'use client';

import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Target, Award, Zap, BarChart2, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Pick } from '@/lib/supabase';
import type { Timeframe, SportFilter, BetTypeFilter } from './ReportingFilters';
import Icon from '@/components/ui/AppIcon';


interface Props {
  timeframe: Timeframe;
  sport: SportFilter;
  betType: BetTypeFilter;
}

export default function ReportingKPIs({ timeframe, sport, betType }: Props) {
  const [picks, setPicks] = useState<Pick[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPicks() {
      setLoading(true);
      try {
        let query = supabase
          .from('picks')
          .select('*')
          .neq('result', 'pending');

        if (sport !== 'all') query = query.eq('sport', sport);
        if (betType !== 'all') query = query.eq('bet_type', betType);

        const { data, error } = await query;
        if (error) throw error;

        const now = new Date();
        const filtered = (data || []).filter((p) => {
          if (timeframe === 'all') return true;
          const date = new Date(p.posted_at);
          const diffDays = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
          if (timeframe === '7d') return diffDays <= 7;
          if (timeframe === '30d') return diffDays <= 30;
          if (timeframe === '90d') return diffDays <= 90;
          if (timeframe === 'ytd') return date.getFullYear() === now.getFullYear();
          return true;
        });

        setPicks(filtered);
      } catch {
        setPicks([]);
      } finally {
        setLoading(false);
      }
    }
    loadPicks();
  }, [timeframe, sport, betType]);

  const wins = picks.filter((p) => p.result === 'win');
  const losses = picks.filter((p) => p.result === 'loss');
  const pushes = picks.filter((p) => p.result === 'push');
  const totalUnits = picks.reduce((a, p) => a + (p.units_result || 0), 0);
  const totalRisked = picks.reduce((a, p) => a + p.units, 0);
  const winPct = picks.length > 0 ? ((wins.length / (wins.length + losses.length)) * 100) : 0;
  const roi = totalRisked > 0 ? (totalUnits / totalRisked) * 100 : 0;
  const avgOdds = picks.length > 0 ? picks.reduce((a, p) => a + p.odds, 0) / picks.length : 0;

  const sorted = [...picks].sort((a, b) => new Date(b.posted_at).getTime() - new Date(a.posted_at).getTime());
  let streak = 0;
  let streakType = '';
  if (sorted.length > 0) {
    streakType = sorted[0].result === 'win' ? 'W' : sorted[0].result === 'loss' ? 'L' : 'P';
    for (const p of sorted) {
      const t = p.result === 'win' ? 'W' : p.result === 'loss' ? 'L' : 'P';
      if (t === streakType) streak++;
      else break;
    }
  }

  const kpis = [
    {
      label: 'Net Units',
      value: loading ? '—' : `${totalUnits >= 0 ? '+' : ''}${totalUnits.toFixed(2)}u`,
      sub: `${totalRisked.toFixed(1)}u risked`,
      icon: TrendingUp,
      color: totalUnits >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400',
      bg: totalUnits >= 0 ? 'bg-[hsl(var(--primary)/0.08)] border-[hsl(var(--primary)/0.2)]' : 'bg-red-500/08 border-red-500/20',
    },
    {
      label: 'Record',
      value: loading ? '—' : `${wins.length}-${losses.length}-${pushes.length}`,
      sub: `${picks.length} picks graded`,
      icon: Target,
      color: 'text-blue-400',
      bg: 'bg-blue-500/08 border-blue-500/20',
    },
    {
      label: 'Win Rate',
      value: loading ? '—' : `${winPct.toFixed(1)}%`,
      sub: 'of graded picks',
      icon: Award,
      color: winPct >= 52.4 ? 'text-[hsl(var(--primary))]' : 'text-amber-400',
      bg: winPct >= 52.4 ? 'bg-[hsl(var(--primary)/0.08)] border-[hsl(var(--primary)/0.2)]' : 'bg-amber-500/08 border-amber-500/20',
    },
    {
      label: 'ROI',
      value: loading ? '—' : `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`,
      sub: 'return on units risked',
      icon: Zap,
      color: roi >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400',
      bg: roi >= 0 ? 'bg-[hsl(var(--primary)/0.08)] border-[hsl(var(--primary)/0.2)]' : 'bg-red-500/08 border-red-500/20',
    },
    {
      label: 'Avg Odds',
      value: loading ? '—' : `${avgOdds >= 0 ? '+' : ''}${avgOdds.toFixed(0)}`,
      sub: 'average line taken',
      icon: BarChart2,
      color: 'text-purple-400',
      bg: 'bg-purple-500/08 border-purple-500/20',
    },
    {
      label: 'Active Streak',
      value: loading ? '—' : streak > 0 ? `${streakType}${streak}` : '—',
      sub: streak > 0 ? (streakType === 'W' ? 'consecutive wins' : 'consecutive losses') : 'no streak',
      icon: TrendingDown,
      color: streakType === 'W' ? 'text-[hsl(var(--primary))]' : streakType === 'L' ? 'text-red-400' : 'text-[hsl(var(--foreground-muted))]',
      bg: streakType === 'W' ? 'bg-[hsl(var(--primary)/0.08)] border-[hsl(var(--primary)/0.2)]' : streakType === 'L' ? 'bg-red-500/08 border-red-500/20' : 'bg-[hsl(var(--card-elevated))] border-[hsl(var(--border))]',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
      {kpis.map((kpi) => {
        const Icon = kpi.icon;
        return (
          <div key={kpi.label} className={`stat-card border ${kpi.bg} flex flex-col gap-3`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-[hsl(var(--foreground-muted))]">{kpi.label}</span>
              <Icon size={14} className={kpi.color} />
            </div>
            <div className="flex flex-col gap-0.5">
              {loading ? (
                <Loader2 size={14} className="animate-spin text-[hsl(var(--foreground-muted))]" />
              ) : (
                <span className={`font-mono text-xl font-bold tabular-nums ${kpi.color}`}>{kpi.value}</span>
              )}
              <span className="text-xs text-[hsl(var(--foreground-muted))]">{kpi.sub}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}